// km-api/dto.js
// Normalizzazione difensiva dei payload backend verso il formato usato dai componenti.
// Principio (Specifica v1.1 §5.2, §9.3): mai inventare valori; "non disponibile"
// esplicito al posto di zero o stringa vuota quando il dato manca.

/**
 * Schema domanda (Specifica v1.1 §8.3 / §6).
 * Campi: id, sezione_id, codice, ordine, testo, testo_prompt, tipo_risposta,
 * obbligatoria, condizione_attivazione, regole_validazione, precompila_da,
 * mappa_destinazione, opzioni.
 */
export function normalizeDomanda(raw) {
  return {
    id: raw.id,
    sezioneId: raw.sezione_id ?? null,
    codice: raw.codice,
    ordine: raw.ordine ?? null,
    testo: raw.testo ?? '',
    testoPrompt: raw.testo_prompt ?? raw.testo ?? '',
    tipoRisposta: raw.tipo_risposta, // vedi TIPO_RISPOSTA_COMPONENTS in questionarioRenderer
    obbligatoria: Boolean(raw.obbligatoria),
    condizioneAttivazione: raw.condizione_attivazione ?? null,
    regoleValidazione: raw.regole_validazione ?? null,
    // Precompilazione SOLO come proposta: non sovrascrivere una risposta esistente (§6)
    precompilaDa: raw.precompila_da ?? null,
    // Metadato informativo: nessuna scrittura diretta dal frontend (§6)
    mappaDestinazione: raw.mappa_destinazione ?? null,
    opzioni: Array.isArray(raw.opzioni) ? raw.opzioni : [],
  };
}

/**
 * Risposta conversazionale (Specifica v1.1 §8.2).
 */
export function normalizeMessaggio(raw) {
  return {
    text: raw.text ?? '',
    attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
    // Corretto 18/09 dopo verifica dal vivo: la forma reale è
    // { type: 'quick_reply', options: [...] } — "options" allo stesso
    // livello di "type", non annidato sotto un'altra chiave "quick_reply".
    // Prima cercava attachment.quick_reply.options, che con questa forma
    // non esiste mai: i bottoni non sarebbero mai comparsi.
    quickReply: raw.attachments?.find((a) => a.type === 'quick_reply')?.options ?? null,
    // Griglia di voti 1–5 a clic (tracker, 23/10): { invio, scala[], voci[{ id, label, voto, voto_precedente }] }.
    // Dove c'è sostituisce i pulsanti; i voti partono insieme in reply_id = invio + JSON {id: voto}.
    griglia: raw.attachments?.find((a) => a.type === 'griglia_voti') ?? null,
    // Griglia di selezione per le risposte multiple (SAL 30/10): { invio, opzioni[{ id, label }] }.
    selezione: raw.attachments?.find((a) => a.type === 'griglia_scelte') ?? null,
    templateId: raw.template_id ?? null,
    templateParams: raw.template_params ?? null,
    // Segnale AUTORITATIVO di chiusura: mai dedotto lato client (§6.2 punto 5)
    closesSession: Boolean(raw.closes_session),
  };
}

/**
 * Record longlist: fit_score è valore esterno, va convertito in modo
 * difensivo; i record senza score restano in fondo (Specifica v1.1 §5.2).
 */
export function normalizeLonglistRecord(raw) {
  const score = Number(raw.fit_score);
  return {
    ...raw,
    fitScore: Number.isFinite(score) ? score : null,
    hasScore: Number.isFinite(score),
  };
}

/**
 * Ordina una longlist mettendo i record senza score in fondo,
 * senza deduplicare candidati (responsabilità backend, §5.2).
 */
export function sortLonglistByScore(records) {
  return [...records].sort((a, b) => {
    if (a.hasScore && !b.hasScore) return -1;
    if (!a.hasScore && b.hasScore) return 1;
    if (!a.hasScore && !b.hasScore) return 0;
    return b.fitScore - a.fitScore;
  });
}
